<template>
    <div
        class="three-sixty-wrapper"
        :class="{ 'is-loading': isLoading }"
    >
        <div class="three-sixty">
            <Image
                v-for="(image, index) in images"
                v-show="index === currentImageIndex"
                :key="index"
                :alt="altGetter(index)"
                :height="416"
                :width="740"
                fetchpriority="high"
                fit="contain"
                loading="eager"
                v-bind="getImageProps(image, ['740px'])"
                @error="() => onload(image.url)"
                @load="() => onload(image.url)"
            />

            <div
                v-show="!isLoading"
                class="input-catcher-wrapper"
            >
                <div
                    ref="inputCatcherEl"
                    class="input-catcher"
                    @mousedown="handleMouseDown"
                    @touchstart="handleTouchStart"
                    @touchmove.prevent.stop="handleTouchMove"
                >
                    <div class="scrolled-content"></div>
                </div>
            </div>
        </div>

        <div class="legend-slot">
            <div
                v-if="!isLoading"
                class="legend"
            >
                {{ rotatitonTip }}
            </div>
            <LoadingAnimation
                v-else
                class="loader"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from 'vue'

import { useThreeSixtyImages } from './useThreeSixtyImages'
import { useThreeSixtyInput } from './useThreeSixtyInput'
import type { ThreeSixtyFragment } from '@/graphql/api'
import { getImageProps } from '@/components/getImageProps.ts'

import Image from '@/components/Image.vue'
import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'
import { useI18n } from 'vue-i18n'
import { useIsMobileAgent } from '@/plugins/is-mobile-agent.ts'

interface ThreeSixtyProps {
    images: ThreeSixtyFragment[]
    initialImageIndex?: number
    externalImageIndex?: number
    altGetter: (index: number) => string
}

const props = withDefaults(defineProps<ThreeSixtyProps>(), {
    initialImageIndex: 0,
    externalImageIndex: undefined,
})

const emit = defineEmits<{
    'index': [number]
}>()

const { images } = toRefs(props)
const loadedImages = ref<Record<string, true>>({})

const onload = (url: string) => {
    loadedImages.value[url] = true
}

const isLoading = computed(() =>
    images.value.some((item) => !(item.url in loadedImages.value))
)

const { currentImageIndex } = useThreeSixtyImages(
    images,
    props.initialImageIndex
)

watch(currentImageIndex, (idx) => {
    emit('index', idx)
})

watch(
    () => props.externalImageIndex,
    (n) => {
        if (undefined !== n && n !== currentImageIndex.value) {
            currentImageIndex.value = n
        }
    }
)

const { handleTouchStart, handleTouchMove, handleMouseDown, inputCatcherEl } =
    useThreeSixtyInput(currentImageIndex)

const { t } = useI18n()
const isMobileAgent = useIsMobileAgent()
const rotatitonTip = computed(() =>
    isMobileAgent ? t('gallery.swipe_to_rotate') : t('gallery.drag_to_rotate')
)
</script>

<style lang="scss" scoped>
.three-sixty-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f9f9f9;
    aspect-ratio: 1;
    overscroll-behavior-y: contain;

    &.is-loading {
        cursor: wait;

        .three-sixty {
            cursor: wait;
        }
    }

    .legend-slot {
        position: absolute;
        bottom: 16px;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        z-index: 1;
        pointer-events: none;

        @include min-width(1024) {
            top: 10px;
            bottom: auto;
        }
    }

    .legend-slot .loader {
        height: auto;
        padding: 0;
    }

    .legend {
        text-align: center;
        font-family: $effra-font-stack;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        font-weight: 400;
        font-size: 0.75rem;
        color: $grey-500;

        &::before {
            content: '';
            background: url('/src/assets/icons/swipe-icon.svg') no-repeat center
                center;
            background-size: 30px auto;
            width: 100%;
            height: 30px;
            display: block;
            opacity: 0.6;
        }
    }

    .three-sixty {
        position: relative;
        max-width: 740px;
        width: 100%;
        height: 416px;
        user-select: none;
        cursor: grab;
        display: flex;
        align-items: center;

        :deep(.image) {
            mix-blend-mode: darken;
        }

        .input-catcher-wrapper {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
        }

        .input-catcher {
            position: absolute;
            top: 0;
            left: 0;
            width: 110%;
            height: 110%;
            overflow: scroll;

            .scrolled-content {
                width: 4000%;
            }
        }
    }
}
</style>
